import { useQuery } from '@tanstack/react-query'
import { Preferences } from '@capacitor/preferences'
import { api } from './api'
import { isNativeApp } from './native/platform'
import { devOrigin, getInstance, originForOrgId } from './native/instance'

/**
 * Native B1 (NB1-9): the anonymous `GET /api/config/public` payload — exactly this shape and
 * nothing else (no customer name, tier or committee). The org ID is public by construction (it
 * is in DNS).
 *
 * On native the last good value is cached per instance under Preferences
 * `orcl:${host}:publicConfig` (it is not secret), so the basemap settings survive a cold start
 * with no signal. On web nothing is cached: the build's `VITE_*` env is the map config.
 */
export interface PublicConfig {
  organizationId: string | null
  apiVersion: number
  map: { protomapsApiKey: string | null; pmtilesUrl: string | null }
}

export const PUBLIC_CONFIG_QUERY_KEY = ['config', 'public'] as const

/** Native in-memory copy of the current instance's last good config, tagged with its host. */
let memory: { host: string; config: PublicConfig } | null = null

function currentHost(): string | null {
  const inst = getInstance()
  return inst ? new URL(inst.origin).host : null
}

function cacheKey(host: string): string {
  return `orcl:${host}:publicConfig`
}

function isPublicConfig(data: unknown): data is PublicConfig {
  return !!data && typeof data === 'object' && typeof (data as PublicConfig).apiVersion === 'number'
}

/** Native: read the current instance's cached config from Preferences into memory. */
async function readCached(host: string): Promise<PublicConfig | null> {
  if (memory?.host === host) return memory.config
  try {
    const { value } = await Preferences.get({ key: cacheKey(host) })
    if (!value) return null
    const parsed: unknown = JSON.parse(value)
    if (!isPublicConfig(parsed)) return null
    memory = { host, config: parsed }
    return parsed
  } catch {
    return null
  }
}

/**
 * The current instance's public config, or null on any error (404, network, bad shape).
 * Native: a good answer is cached; with no answer at all (offline — no HTTP response) the cached
 * value for this instance is returned instead. An HTTP error or a non-config body is never
 * papered over with the cache: the existence check in LoginPage/JoinPage relies on that null.
 */
export async function loadPublicConfig(): Promise<PublicConfig | null> {
  const host = isNativeApp() ? currentHost() : null
  try {
    const { data } = await api.get<PublicConfig>('/config/public')
    if (!isPublicConfig(data)) return null
    // A late answer for an instance the user has since left (org switch) must not rewrite the
    // cache that switch just cleared.
    if (host && currentHost() === host) {
      memory = { host, config: data }
      await Preferences.set({ key: cacheKey(host), value: JSON.stringify(data) }).catch(() => {})
    }
    return data
  } catch (err) {
    const offline = !(err as { response?: unknown } | null)?.response
    if (host && offline) return readCached(host)
    return null
  }
}

/**
 * [M1] Does this config answer for `orgId`? A server that names a DIFFERENT organization (a DNS /
 * proxy misroute) is not that organization: a password or code must never go to the wrong
 * customer's box. A config with no organizationId cannot prove a mismatch, so it passes; so does
 * any answer while the dev escape is honoured (every org ID then resolves to the one tester box).
 */
export function configMatchesOrg(config: PublicConfig, orgId: string): boolean {
  if (!config.organizationId || devOrigin()) return true
  return config.organizationId === orgId
}

/**
 * [M3] Native existence check for a CANDIDATE organization, run before anything is switched: a
 * typo'd org prefix must not sign the organizer out or clear the current instance. Goes to the
 * candidate's absolute origin, so the request interceptor attaches no bearer (M2) — the current
 * instance's credential never reaches another server. Nothing is cached. Null on any error.
 */
export async function probePublicConfig(orgId: string): Promise<PublicConfig | null> {
  try {
    const { data } = await api.get<PublicConfig>(`${originForOrgId(orgId)}/api/config/public`)
    return isPublicConfig(data) && configMatchesOrg(data, orgId) ? data : null
  } catch {
    return null
  }
}

/** Native start-up: load the cached config for the hydrated instance (no network). No-op on web. */
export async function hydratePublicConfig(): Promise<void> {
  if (!isNativeApp()) return
  memory = null
  const host = currentHost()
  if (host) await readCached(host)
}

/** NB1-13: a permitted organization switch drops the old instance's cached config. */
export async function clearCachedPublicConfig(): Promise<void> {
  if (!isNativeApp()) return
  const host = currentHost()
  memory = null
  if (host) await Preferences.remove({ key: cacheKey(host) }).catch(() => {})
}

export interface MapTileSettings {
  apiKey?: string
  pmtilesUrl?: string
}

let lastSettings: MapTileSettings = {}

/**
 * The basemap tile settings for this instance. Web: the build's `VITE_*` env (unchanged). Native:
 * the cached public config — never the build env, which would be another customer's key; with no
 * config yet it is empty (maps render blank and warn). The returned object keeps its identity
 * until a value changes, so callers can memoize on it.
 */
export function getMapTileSettings(): MapTileSettings {
  let apiKey: string | undefined
  let pmtilesUrl: string | undefined
  if (isNativeApp()) {
    const host = currentHost()
    const map = host && memory?.host === host ? memory.config.map : null
    apiKey = map?.protomapsApiKey ?? undefined
    pmtilesUrl = map?.pmtilesUrl ?? undefined
  } else {
    apiKey = import.meta.env.VITE_PROTOMAPS_API_KEY
    pmtilesUrl = import.meta.env.VITE_PMTILES_URL
  }
  if (lastSettings.apiKey !== apiKey || lastSettings.pmtilesUrl !== pmtilesUrl) {
    lastSettings = { apiKey, pmtilesUrl }
  }
  return lastSettings
}

/** Fixed for the server process's lifetime, so staleTime is Infinity. */
export function usePublicConfig() {
  return useQuery<PublicConfig | null>({
    queryKey: PUBLIC_CONFIG_QUERY_KEY,
    queryFn: loadPublicConfig,
    staleTime: Infinity,
  })
}
